import { useState } from 'react';
import { api, ApiError } from '../lib/api';

export interface SignedInUser {
  username: string;
  displayName: string | null;
  isAdmin: boolean;
  mustChangePassword: boolean;
}

/**
 * Sign-in, matching the credential model of the Billboard Intelligence build so
 * the team carries one login across Harkey tools.
 *
 * The form deliberately reports one message for every failure — wrong password,
 * unknown user, disabled account. Distinguishing them turns the page into a way
 * to find out who works here.
 */
export function Login({ onSignedIn }: { onSignedIn: (user: SignedInUser) => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { user } = await api.post<{ user: SignedInUser }>('/api/auth/login', {
        username,
        password,
      });
      onSignedIn(user);
    } catch (err: unknown) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Could not reach the server. Check your connection and try again.',
      );
      setPassword('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="flex min-h-full items-center justify-center bg-neutral-100 px-4 py-12 dark:bg-neutral-950">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <span className="inline-block bg-black px-3 py-1.5 text-xl font-bold tracking-tight text-white">
            Harkey<span className="text-harkey-gold">Media</span>
          </span>
          <h1 className="mt-4 text-sm font-semibold text-neutral-700 dark:text-neutral-300">
            Install Tracker
          </h1>
        </div>

        <form
          onSubmit={submit}
          className="border border-neutral-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-900"
        >
          {error !== null && (
            <p
              role="alert"
              className="mb-4 border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200"
            >
              {error}
            </p>
          )}

          <label className="mb-3 block text-xs font-medium text-neutral-600 dark:text-neutral-400">
            Username
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoFocus
              required
              className="mt-1 w-full border border-neutral-300 px-2 py-1.5 text-sm dark:border-neutral-700 dark:bg-neutral-950"
            />
          </label>

          <label className="mb-5 block text-xs font-medium text-neutral-600 dark:text-neutral-400">
            Password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              className="mt-1 w-full border border-neutral-300 px-2 py-1.5 text-sm dark:border-neutral-700 dark:bg-neutral-950"
            />
          </label>

          <button
            type="submit"
            disabled={busy || username === '' || password === ''}
            className="w-full bg-harkey-gold py-2 text-sm font-semibold text-black disabled:opacity-50"
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="mt-4 text-center text-xs text-neutral-500">
          Same credentials as Billboard Intelligence.
        </p>
      </div>
    </main>
  );
}

/**
 * Forced password change.
 *
 * Seeded accounts land here and can go nowhere else — a temporary password that
 * is never changed is just a password that several people know.
 */
export function ChangePassword({ onDone }: { onDone: () => void }) {
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const mismatch = confirm !== '' && confirm !== newPassword;

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (mismatch) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/auth/change-password', { currentPassword, newPassword });
      onDone();
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the server.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="flex min-h-full items-center justify-center bg-neutral-100 px-4 py-12 dark:bg-neutral-950">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <span className="inline-block bg-black px-3 py-1.5 text-xl font-bold tracking-tight text-white">
            Harkey<span className="text-harkey-gold">Media</span>
          </span>
          <h1 className="mt-4 text-sm font-semibold">Choose a new password</h1>
          <p className="mt-1 text-xs text-neutral-500">
            Your account is using a temporary password.
          </p>
        </div>

        <form
          onSubmit={submit}
          className="border border-neutral-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-900"
        >
          {error !== null && (
            <p
              role="alert"
              className="mb-4 border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200"
            >
              {error}
            </p>
          )}

          <label className="mb-3 block text-xs font-medium text-neutral-600 dark:text-neutral-400">
            Temporary password
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
              className="mt-1 w-full border border-neutral-300 px-2 py-1.5 text-sm dark:border-neutral-700 dark:bg-neutral-950"
            />
          </label>

          <label className="mb-3 block text-xs font-medium text-neutral-600 dark:text-neutral-400">
            New password
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNew(e.target.value)}
              autoComplete="new-password"
              minLength={12}
              required
              className="mt-1 w-full border border-neutral-300 px-2 py-1.5 text-sm dark:border-neutral-700 dark:bg-neutral-950"
            />
            <span className="mt-1 block text-neutral-500">At least 12 characters.</span>
          </label>

          <label className="mb-5 block text-xs font-medium text-neutral-600 dark:text-neutral-400">
            Confirm new password
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
              className="mt-1 w-full border border-neutral-300 px-2 py-1.5 text-sm dark:border-neutral-700 dark:bg-neutral-950"
            />
            {mismatch && <span className="mt-1 block text-red-600">Passwords do not match.</span>}
          </label>

          <button
            type="submit"
            disabled={busy || mismatch || newPassword.length < 12}
            className="w-full bg-harkey-gold py-2 text-sm font-semibold text-black disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Set password'}
          </button>
        </form>
      </div>
    </main>
  );
}
